import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    ChangeDetectorRef,
} from '@angular/core'
import { UploadService } from '../../../shared/services/upload.service'
import { map, Observable, Subject, takeUntil } from 'rxjs'
import { combineLatest } from 'rxjs'
import { Store } from '@ngrx/store'
import { casesActions } from '../../../state/cases/cases.actions'
import {
    closeCaseManagementDialog,
    openCaseManagementDialog,
} from '../../../shared/utils/case-management-dialog-utils'
import { ToasterService } from '../../../shared/services/toaster.service'
import { HttpErrorResponse } from '@angular/common/http'
import { UpClientFile } from '../../../shared/model/up-client-type-mappings'

@Component({
    selector: 'app-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.scss'],
})
export class UploadComponent implements AfterViewInit, OnDestroy {
    @ViewChild('filePicker')
    filePicker?: ElementRef<HTMLInputElement>

    @ViewChild('dropZone')
    dropZone?: ElementRef<HTMLDivElement>
    isMultiUploadMode = false
    createDatasetsPerFolder = false

    readonly uploads$ = combineLatest({
        current: this.uploadService.currentUploads$,
        completed: this.uploadService.completedUploads$,
    }).pipe(
        map(uploads => {
            const currentUploads = uploads.current?.reduce(
                (filesUnique: UpClientFile[], file: UpClientFile): UpClientFile[] => {
                    return filesUnique.find(f => f.uniqueIdentifier === file.uniqueIdentifier)
                        ? filesUnique
                        : [...filesUnique, file]
                },
                []
            )

            const completedUploads = uploads.completed?.reduce(
                (filesUnique: UpClientFile[], file: UpClientFile): UpClientFile[] => {
                    return uploads.current?.find(
                        f => f.uniqueIdentifier === file.uniqueIdentifier
                    ) || filesUnique.find(f => f.uniqueIdentifier === file.uniqueIdentifier)
                        ? filesUnique
                        : [...filesUnique, file]
                },
                []
            )
            const combined = []
            if (currentUploads) {
                combined.push(...currentUploads)
            }
            if (completedUploads) {
                combined.push(...completedUploads)
            }

            const completedUploadsGroup: { name: string; uploads: UpClientFile[] | undefined }[] =
                []
            const toplevelFile: UpClientFile[] = []

            combined?.forEach((u: UpClientFile) => {
                const relativePath = u.relative_path || u.relativePath
                if (!relativePath.includes('/')) {
                    toplevelFile.push(u)
                    return
                }
                const groupName = relativePath.split('/')[0]
                const index = completedUploadsGroup.findIndex(g => g.name === groupName)
                if (index < 0) {
                    completedUploadsGroup.push({ name: groupName, uploads: [u] })
                } else {
                    completedUploadsGroup[index].uploads!.push(u)
                }
            })

            this.createDatasetsPerFolder =
                this.isMultiUploadMode && toplevelFile.length === 0 ? true : false

            return {
                current: currentUploads,
                // When an upload completes and uploads.completed is reloaded, the same upload
                //  is contained in both current and completed. So it is filtered out of completed here.
                completed: completedUploads,
                toplevelFile: toplevelFile,
                completedGroup: completedUploadsGroup.sort((a, b) => a.name.localeCompare(b.name)),
            }
        })
    )

    readonly hasCompletedUploads$: Observable<boolean> = this.uploads$.pipe(
        map(
            ({ completed, current }) =>
                (completed?.length ?? 0) > 0 ||
                (current?.some(upload => upload.progress() === 1) ?? false)
        )
    )

    private readonly destroy$ = new Subject<void>()

    constructor(
        readonly uploadService: UploadService,
        private readonly store: Store,
        private readonly toaster: ToasterService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.uploads$.subscribe(_ => {
            this.cdr.detectChanges() // Manually trigger change detection
        })
    }

    ngAfterViewInit(): void {
        this.uploadService.uploadServiceIsReady$
            .pipe(takeUntil(this.destroy$))
            .subscribe((isReady: boolean) => {
                if (!isReady) return
                if (this.filePicker?.nativeElement)
                    this.uploadService.setBrowseElement(this.filePicker.nativeElement)
                if (this.dropZone?.nativeElement)
                    this.uploadService.setDropElement(this.dropZone.nativeElement)
            })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    openFilePicker(e: MouseEvent): void {
        // upload lib opens folder picker dialog on its own, this prevents it from opening multiple times
        e.stopImmediatePropagation()
        this.filePicker?.nativeElement.click()
    }

    submitDataset(): void {
        this.uploadService
            .submitDataset(this.isMultiUploadMode, this.createDatasetsPerFolder)
            .subscribe({
                next: () => {
                    this.store.dispatch(casesActions.loadCases())
                    closeCaseManagementDialog()
                    openCaseManagementDialog()
                },
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Error submitting dataset.')
                },
            })
    }

    toggleUploadMode(e: any): void {
        e.stopImmediatePropagation()
        this.isMultiUploadMode = e.currentTarget.checked
        this.uploads$.subscribe(
            u =>
                (this.createDatasetsPerFolder =
                    this.isMultiUploadMode && u.toplevelFile.length === 0 ? true : false)
        )
    }
}
